import { format, isValid, parseISO } from "date-fns";
import type { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";

export const UNKNOWN_UPLOAD_DAY_KEY = "unknown";

export type FileUploadDayGroup = {
  dayKey: string;
  label: string;
  files: SwivuFile[];
};

/** CompanyCam-style day title: “Thursday, October 15th, 2026”. */
export function formatUploadDayLabel(date: Date): string {
  return format(date, "EEEE, MMMM do, yyyy");
}

function resolveUploadDay(createdAt: string | undefined | null): { dayKey: string; label: string } {
  if (!createdAt) {
    return { dayKey: UNKNOWN_UPLOAD_DAY_KEY, label: "Unknown date" };
  }
  const date = parseISO(createdAt);
  if (!isValid(date)) {
    const fallback = new Date(createdAt);
    if (!isValid(fallback)) {
      return { dayKey: UNKNOWN_UPLOAD_DAY_KEY, label: "Unknown date" };
    }
    return {
      dayKey: format(fallback, "yyyy-MM-dd"),
      label: formatUploadDayLabel(fallback),
    };
  }
  return {
    dayKey: format(date, "yyyy-MM-dd"),
    label: formatUploadDayLabel(date),
  };
}

/**
 * Groups files by local calendar day of `createdAt` (upload time).
 * Preserves encounter order so callers can keep server sort (e.g. newest first).
 * Files without a usable date land in a trailing "Unknown date" group.
 */
export function groupFilesByUploadDay(files: SwivuFile[]): FileUploadDayGroup[] {
  const groups = new Map<string, FileUploadDayGroup>();
  let unknownGroup: FileUploadDayGroup | null = null;

  for (const file of files) {
    const { dayKey, label } = resolveUploadDay(file.createdAt);
    if (dayKey === UNKNOWN_UPLOAD_DAY_KEY) {
      if (!unknownGroup) {
        unknownGroup = { dayKey, label, files: [] };
      }
      unknownGroup.files.push(file);
      continue;
    }
    const existing = groups.get(dayKey);
    if (existing) {
      existing.files.push(file);
    } else {
      groups.set(dayKey, { dayKey, label, files: [file] });
    }
  }

  const result = Array.from(groups.values());
  if (unknownGroup) result.push(unknownGroup);
  return result;
}
